import http from "../../utils/http-common";

export interface ArtifactItem {
  name: string;
  path: string;
  type: "file" | "folder";
  size?: number;
  content_type?: string;
  timeCreated?: string;
  updated?: string;
  metadata?: Record<string, string>;
  versions?: any[];
}

export const listStorages = (): Promise<string[]> =>
  http.get<string[]>("/storage").then((r) => r.data);

export const listObjects = (
  bucket: string,
  prefix = ""
): Promise<ArtifactItem[]> =>
  http
    .get<ArtifactItem[]>(`/storage/${bucket}/objects`, { params: { prefix } })
    .then((r) => r.data);

export const searchObjects = (
  bucket: string,
  prefix: string,
  term: string
): Promise<ArtifactItem[]> =>
  http
    .get<ArtifactItem[]>(`/storage/${bucket}/objects/search`, {
      params: { prefix, term },
    })
    .then((r) => r.data);

export const generateSignedUrl = (
  bucket: string,
  path: string,
  method = "GET",
  expiration = 600,
  contentType?: string,
  disposition?: string,
  generation?: string
): Promise<string> => {
  const encodedPath = path
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  return http
    .get<{ url: string }>(
      `/storage/${bucket}/objects/${encodedPath}/signed-url`,
      {
        params: {
          method,
          expiration,
          ...(contentType ? { "Content-Type": contentType } : {}),
          ...(disposition ? { disposition } : {}),
          ...(generation ? { generation } : {}),
        },
      }
    )
    .then((r) => r.data.url);
};

export const uploadObject = (
  bucket: string,
  file: File,
  prefix = "",
  meta_data: Record<string, any> = {}
): Promise<ArtifactItem> => {
  const form = new FormData();
  form.append("file", file);
  form.append("prefix", prefix);
  form.append("meta_data", JSON.stringify(meta_data));
  return http
    .post<ArtifactItem>(`/storage/${bucket}/objects`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};

export const deleteObject = (bucket: string, path: string): Promise<void> => {
  const encodedPath = path
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  return http
    .delete(`/storage/${bucket}/objects/${encodedPath}`)
    .then(() => {});
};

export const generateUploadSignedUrl = (
  bucket: string,
  path: string,
  expiration = 600
): Promise<string> => {
  const encodedPath = path
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  return http
    .get<{ url: string }>(
      `/storage/${bucket}/objects/${encodedPath}/upload-url`,
      {
        params: { expiration },
      }
    )
    .then((r) => r.data.url);
};

export const getArtifactTree = (bucket: string): Promise<ArtifactItem[]> =>
  http.get(`/storage/${bucket}/tree`).then((r) => r.data);

export const syncBucket = (
  bucket: string
): Promise<{ new: number; updated: number }> =>
  http.get(`/storage/${bucket}/sync`).then((r) => r.data);

export const searchObjectsPaginated = (bucket, prefix, term, page, pageSize) =>
  http
    .get(`/storage/${bucket}/objects/search`, {
      params: { prefix, term, page, page_size: pageSize },
    })
    .then((r) => r.data);

export const fetchSearchTreeObjects = (bucket, currentPath, term) =>
  http
    .get(`/storage/${bucket}/objects/search-tree`, {
      params: { prefix: currentPath, term },
    })
    .then((r) => r.data);
